import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useInDesktopShell, useIsDesktopRoot } from '@/shared/lib/desktopLayout';
import { cn } from '@/shared/lib/cn';
import { BackGesture } from './BackGesture';
import { ScrollArea } from './ScrollArea';

interface PageHeaderProps {
  /**
   * Строка — обычный заголовок `<h1>`. Элемент — ставится на то же место как
   * есть: так в шапке главной вместо названия стоит переключатель языка (см.
   * `LearningLanguageSwitcher`), на той же высоте и по тому же центру.
   */
  title: ReactNode;
  /** Есть куда вернуться — включаем нативную кнопку Telegram (в шапке своей стрелки нет, она дублировала бы системную). */
  onBack?: () => void;
  /** Правый угол шапки (например, значок настроек) — по умолчанию пусто. */
  action?: ReactNode;
  /**
   * Левый угол шапки. Обычно пуст — там нативная «назад» Telegram. Занимать его
   * стоит только на экране, где системная кнопка выключена
   * (`nativeBackButton={false}` и без `BackGesture` у родителя), иначе они
   * наложатся: так сделано у проверки уровня, где слева крестик «прервать».
   */
  leading?: ReactNode;
  /**
   * false — эта шапка не трогает нативную кнопку сама, её уже держит
   * родитель одним вызовом `useTelegramBackButton` на весь срок жизни
   * страницы. Нужно там, где список и деталь — разные `PageHeader`
   * (разные React-компоненты: `MyListsManagePage`/`ListDetailView`,
   * `MistakesPage`/`RuleDetail`), и переключение между ними размонтирует
   * один `PageHeader` и монтирует другой — Telegram успевает на мгновение
   * отрисовать крестик между hide() размонтированного и show() нового,
   * прежде чем перерисовать стрелку.
   */
  nativeBackButton?: boolean;
}

/**
 * Шапка страницы в одном месте: заголовок всегда на одной высоте и с одним
 * отступом, вместо того чтобы каждая страница собирала своё — как было с
 * локальным `Header` в MistakesPage.
 *
 * `fixed` в полосе `--status-bar-top`/`--header-band-height`, а не в потоке
 * страницы: заголовок должен стоять на одном уровне с нативными back/close
 * Telegram (та же полоса, что клиент резервирует под свои кнопки), а не
 * ниже них отдельной строкой. Контент страницы всё равно отступает от
 * `--safe-top` (см. `PageLayout`) — эта полоса ему не принадлежит и так.
 *
 * Три колонки грида, а не флекс с `flex-1` на заголовке: тогда заголовок
 * смещался бы к правому `action`, а не стоял по центру экрана. Левая колонка
 * пустая (назад — только нативная кнопка Telegram, свою не дублируем), но
 * зарезервирована той же `minmax(2.5rem, auto)`, что и правая — иначе
 * заголовок центрировался бы между пустым краем и `action`, а не по центру
 * экрана.
 */
export function PageHeader({ title, onBack: onBackProp, action, leading, nativeBackButton = true }: PageHeaderProps) {
  // Раздел сайдбара на компьютере — возвращаться некуда (см. `DesktopRootsContext`).
  const onBack = useIsDesktopRoot() ? undefined : onBackProp;
  // enabled=false, когда возвращаться некуда (корень таба) или когда
  // нативную кнопку и свайп уже держит родитель.
  const backEnabled = nativeBackButton && Boolean(onBack);
  const { t } = useTranslation('BackGesture');

  // Компьютер (десктопный каркас веб-версии): заголовка-текста нет вовсе —
  // где страница корневая, раздел и так подсвечен в сайдбаре; где есть куда
  // вернуться, на месте заголовка — «← Назад» обычным, не жирным шрифтом
  // (решение Дмитрия, 2026-10-26). Заголовок-элемент (переключатель языка на
  // главной) остаётся: это управление, а не подпись. Высота — та же
  // переменная `--header-band-height`, спейсеры страниц не меняются.
  if (useInDesktopShell()) {
    const element = typeof title === 'string' ? null : title;
    return (
      <>
        <BackGesture enabled={backEnabled} onBack={onBack ?? noop} />
        <div className="fixed inset-x-0 top-[var(--status-bar-top)] z-30 flex h-[var(--header-band-height)] items-center gap-2 bg-[var(--color-bg)] px-6">
          {leading ??
            (onBack && (
              <button
                type="button"
                onClick={onBack}
                className="-ml-3 flex shrink-0 items-center gap-1 rounded-full py-2 pr-3 pl-2 text-base font-medium text-ink hover:bg-ink/5 active:bg-ink/10"
              >
                <ChevronLeft className="size-5" aria-hidden="true" />
                {t('back')}
              </button>
            ))}
          <div className="flex min-w-0 flex-1">{element}</div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      </>
    );
  }

  return (
    <>
      <BackGesture enabled={backEnabled} onBack={onBack ?? noop} />
      <div
        // bg-[var(--color-bg)] — фон страницы, а не прозрачный: заголовок вынесен
        // из потока (`fixed`), и без своего фона контент при скролле проезжал бы
        // визуально под ним, наслаиваясь на title.
        className="fixed inset-x-0 top-[var(--status-bar-top)] z-30 grid h-[var(--header-band-height)] grid-cols-[minmax(2.5rem,auto)_1fr_minmax(2.5rem,auto)] items-center gap-2 bg-[var(--color-bg)] px-4"
      >
        {leading ? <div className="justify-self-start">{leading}</div> : <div aria-hidden="true" />}
        {typeof title === 'string' ? (
          <h1 className="truncate text-center text-xl font-extrabold">{title}</h1>
        ) : (
          <div className="flex min-w-0 justify-center">{title}</div>
        )}
        <div className="justify-self-end">{action}</div>
      </div>
    </>
  );
}

function noop() {
  // См. комментарий у useTelegramBackButton выше.
}

interface PageLayoutProps {
  /** См. `PageHeaderProps.title`. */
  title?: ReactNode;
  onBack?: () => void;
  headerAction?: ReactNode;
  /** См. `PageHeaderProps.nativeBackButton`. */
  nativeBackButton?: boolean;
  className?: string;
  children: ReactNode;
  /**
   * Закреплённый низ — основная кнопка экрана, прибитая к нижнему краю: контент
   * прокручивается над ней, а она всегда на месте. Только для экранов без
   * таббара (у таб-страниц низ занят им).
   */
  footer?: ReactNode;
}

/**
 * Общий каркас страниц-табов. Заголовок (см. `PageHeader`) — `fixed` полоса
 * поверх контента на уровне нативных back/close Telegram, поэтому в потоке
 * страницы под него нужен свой спейсер той же высоты (`--status-bar-top` +
 * `--header-band-height`) — без него `ScrollArea` растягивалась бы от самого
 * верха экрана, и при скролле контент физически заезжал бы под шапку (одного
 * непрозрачного фона у шапки недостаточно: над ней, в системном вырезе,
 * фона нет вообще). Спейсер — не просто отступ у контента, а именно
 * отдельный элемент до `ScrollArea`: тогда скролл-контейнер сам по себе
 * начинается ниже шапки, и заехать под неё контенту физически некуда.
 * Заголовок опционален — без него работает старый отступ от `--safe-top`
 * (нужно для страниц без общего заголовка, например деталей с собственной
 * навигацией).
 */
export function PageLayout({
  title,
  onBack,
  headerAction,
  nativeBackButton,
  className,
  children,
  footer,
}: PageLayoutProps) {
  // В каркасе — поля как у шапки (`px-6`), иначе контент и заголовок не на одной линии.
  const desktop = useInDesktopShell();
  const atDesktopRoot = useIsDesktopRoot();
  // На компьютере текстового заголовка нет (см. `PageHeader`): без «назад» и
  // кнопки справа шапке нечего показать — ни её, ни места под неё. Иначе
  // сверху корневых разделов зияла пустая полоса.
  const headerShown =
    Boolean(title) &&
    !(desktop && typeof title === 'string' && (!onBack || atDesktopRoot) && !headerAction);
  return (
    <main className="flex h-dvh flex-col text-ink">
      {headerShown && (
        <>
          <PageHeader
            title={title}
            onBack={onBack}
            action={headerAction}
            nativeBackButton={nativeBackButton}
          />
          <div
            aria-hidden="true"
            className="h-[calc(var(--status-bar-top)+var(--header-band-height))] shrink-0"
          />
        </>
      )}
      <ScrollArea
        className={desktop ? 'px-6' : 'px-4'}
        contentClassName={cn(
          // min-h-full: короткие страницы (например HomePage) держат `mt-auto`
          // у последнего блока прижатым к низу видимой области, а не к своей
          // естественной высоте.
          'flex min-h-full flex-col gap-4',
          // С закреплённым низом отступ под таббар не нужен: низ экрана —
          // это сам `footer`, и он уже учитывает `--safe-bottom`.
          footer ? 'pb-4' : 'pb-[calc(var(--safe-bottom)+var(--tabbar-height)+1.5rem)]',
          headerShown ? 'pt-4' : 'pt-[calc(var(--safe-top)+1.5rem)]',
          className,
        )}
      >
        {children}
      </ScrollArea>
      {footer && (
        // На компьютере главная кнопка экрана — разумной ширины по центру,
        // а не полоса во всю колонку.
        <div
          className={cn(
            'shrink-0 px-5 pt-2 pb-[calc(var(--safe-bottom)+1rem)]',
            desktop && 'mx-auto w-full max-w-sm pb-[calc(var(--safe-bottom)+1.5rem)]',
          )}
        >
          {footer}
        </div>
      )}
    </main>
  );
}
